import React, { useState, useEffect, useRef } from 'react';
import { History, Trash2, X, Clock } from 'lucide-react';
import { useLanguage } from '../i18n';

const STORAGE_KEY = 'json-format-history';
const MAX_HISTORY = 10;

/**
 * 历史记录管理工具
 */
export const historyManager = {
  // 获取所有历史记录
  getAll() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (err) {
      console.error('Failed to load history:', err);
      return [];
    }
  },

  // 添加新记录
  add(jsonContent) {
    try {
      const history = this.getAll();

      // 检查是否与最新的一条记录相同（避免重复）
      if (history.length > 0 && history[0].content === jsonContent) {
        return;
      }

      const newRecord = {
        id: Date.now(),
        content: jsonContent,
        preview: this.generatePreview(jsonContent),
        timestamp: this.formatTimestamp(new Date())
      };

      // 添加到开头，并限制数量
      const updatedHistory = [newRecord, ...history].slice(0, MAX_HISTORY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (err) {
      console.error('Failed to save history:', err);
    }
  },

  // 删除单条记录
  remove(id) {
    try {
      const history = this.getAll();
      const updatedHistory = history.filter(item => item.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch (err) {
      console.error('Failed to remove history:', err);
    }
  },

  // 清空所有记录
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  },

  // 生成预览文本（前 80 字符）
  generatePreview(content) {
    const text = content.trim().replace(/\s+/g, ' ');
    return text.length > 80 ? text.substring(0, 80) + '...' : text;
  },

  // 格式化时间戳
  formatTimestamp(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
};

/**
 * 历史记录面板组件
 */
function HistoryPanel({ onLoad, version }) {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const panelRef = useRef(null);

  // 加载历史记录
  const loadHistory = () => {
    const records = historyManager.getAll();
    setHistory(records);
  };

  // 初始加载历史记录（用于显示数字徽章）
  useEffect(() => {
    loadHistory();
  }, []);

  // 监听版本变化，实时更新历史记录
  useEffect(() => {
    if (version > 0) {
      loadHistory();
    }
  }, [version]);

  // 点击外部关闭面板
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      loadHistory(); // 打开时刷新数据
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 加载历史记录
  const handleLoad = (record) => {
    if (onLoad) {
      onLoad(record.content);
    }
    setIsOpen(false);
  };

  // 删除单条记录
  const handleDelete = (e, id) => {
    e.stopPropagation();
    historyManager.remove(id);
    loadHistory();
  };

  // 清空所有记录
  const handleClearAll = () => {
    if (window.confirm(language === 'zh' ? '确定要清空所有历史记录吗？' : 'Clear all history?')) {
      historyManager.clear();
      loadHistory();
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* 历史记录按钮 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-all duration-200 flex items-center gap-2 ${
          isOpen
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600'
            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
        }`}
      >
        <History size={16} />
        {t('json.history.title')}
        {history.length > 0 && (
          <span className="bg-indigo-500 text-white text-xs rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
            {history.length}
          </span>
        )}
      </button>

      {/* 下拉面板 */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-[500px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 max-h-[500px] flex flex-col">
          {/* 头部 */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <History size={16} />
              {t('json.history.title')}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* 历史记录列表 */}
          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                <History size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">{t('json.history.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {history.map((record, index) => (
                  <div
                    key={record.id}
                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer group"
                    onClick={() => handleLoad(record)}
                  >
                    <div className="flex items-start gap-3">
                      {/* 序号 */}
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>

                      {/* 内容 */}
                      <div className="flex-1 min-w-0">
                        {/* 预览 */}
                        <p className="text-sm text-gray-800 dark:text-gray-200 font-mono truncate mb-1">
                          {record.preview}
                        </p>
                        {/* 时间 */}
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock size={12} />
                          {record.timestamp}
                        </div>
                      </div>

                      {/* 删除按钮 */}
                      <button
                        onClick={(e) => handleDelete(e, record.id)}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="删除"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 底部：清空按钮 */}
          {history.length > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={handleClearAll}
                className="w-full px-3 py-2 text-sm font-medium bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={14} />
                清空所有记录
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HistoryPanel;
